// lib/leasing/dealIncentiveEnvelope.ts
//
// THE ONE PLACE THAT PRICES A LANDLORD INCENTIVE ENVELOPE.
// rentAmount on a Deal is an ANNUAL rent (see DECIDE_BASE_RENT doctrine: "default to
// ANNUAL_FIXED"), so every month-denominated concession (rent-free, step rents) is priced
// against annualRent / 12, while the fit-out contribution is an absolute amount already.
// The reference semantics are the ones the Deal page shows on screen (NER block).

type NumericLike = number | string | { toString(): string } | null | undefined;

type IncentiveStepLike = {
  startMonth?: number | null;
  endMonth?: number | null;
  rentPercent?: number | null;
} | null | undefined;

type DealIncentiveEnvelopeInput = {
  /** Annual base rent. Monthly figures are derived from it, never the other way round. */
  annualRent?: NumericLike;
  rentFreeMonths?: NumericLike;
  fitOutAmount?: NumericLike;
  steps?: readonly IncentiveStepLike[] | null;
};

type DealIncentiveEnvelope = {
  monthlyRent: number;
  rentFreeValue: number;
  fitOutValue: number;
  stepDiscountValue: number;
  totalIncentiveValue: number;
};

/** Tolerates null, undefined and Decimal-like values, exactly like the call sites did inline. */
const toFiniteNumber = (value: NumericLike): number => {
  const parsed = typeof value === 'number' ? value : Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function computeDealIncentiveEnvelope(input: DealIncentiveEnvelopeInput): DealIncentiveEnvelope {
  const monthlyRent = toFiniteNumber(input.annualRent) / 12;
  const rentFreeValue = toFiniteNumber(input.rentFreeMonths) * monthlyRent;
  const fitOutValue = toFiniteNumber(input.fitOutAmount);

  let stepDiscountValue = 0;
  if (Array.isArray(input.steps)) {
    for (const step of input.steps) {
      if (!step) continue;
      const startMonth = toFiniteNumber(step.startMonth);
      const endMonth = toFiniteNumber(step.endMonth);
      const months = (endMonth - startMonth) + 1;
      if (months <= 0) continue;
      stepDiscountValue += months * monthlyRent * (1 - (toFiniteNumber(step.rentPercent) / 100));
    }
  }

  return {
    monthlyRent,
    rentFreeValue,
    fitOutValue,
    stepDiscountValue,
    totalIncentiveValue: rentFreeValue + fitOutValue + stepDiscountValue,
  };
}
